import {
  OrgTreeSchema,
  PeopleListResponseSchema,
  PersonAccessSchema,
  PersonDetailSchema,
  QueueCountsResponseSchema,
  QueueListResponseSchema,
  SiteDetailSchema,
  type OrgTree,
  type PeopleListQuery,
  type PeopleListResponse,
  type PersonAccess,
  type PersonDetail,
  type QueueCountsResponse,
  type QueueKind,
  type QueueListResponse,
  type SiteDetail,
} from '@aroo/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiGet } from './client';

/** Query keys for the People section. */
export const peopleKeys = {
  list: (filters: Omit<PeopleListQuery, 'cursor'>) => ['people', 'list', filters] as const,
  person: (id: string) => ['people', 'person', id] as const,
  personAccess: (id: string) => ['people', 'person', id, 'access'] as const,
  orgTree: ['org', 'tree'] as const,
  site: (code: string) => ['org', 'site', code] as const,
  queues: ['queues'] as const,
  queue: (kind: QueueKind) => ['queues', kind] as const,
};

export function getPeople(query: PeopleListQuery): Promise<PeopleListResponse> {
  return apiGet('/people', PeopleListResponseSchema, query);
}

export function getPerson(id: string): Promise<PersonDetail> {
  return apiGet(`/people/${encodeURIComponent(id)}`, PersonDetailSchema);
}

export function getPersonAccess(id: string): Promise<PersonAccess> {
  return apiGet(`/people/${encodeURIComponent(id)}/access`, PersonAccessSchema);
}

export function getOrgTree(): Promise<OrgTree> {
  return apiGet('/org/tree', OrgTreeSchema);
}

export function getSite(code: string): Promise<SiteDetail> {
  return apiGet(`/org/sites/${encodeURIComponent(code)}`, SiteDetailSchema);
}

export function getQueueCounts(): Promise<QueueCountsResponse> {
  return apiGet('/queues', QueueCountsResponseSchema);
}

export function getQueue(kind: QueueKind): Promise<QueueListResponse> {
  return apiGet(`/queues/${kind}`, QueueListResponseSchema);
}

/** Keyset-paged People list: each page carries the cursor for the next. */
export function usePeopleList(filters: Omit<PeopleListQuery, 'cursor'>) {
  return useInfiniteQuery({
    queryKey: peopleKeys.list(filters),
    queryFn: ({ pageParam }) =>
      getPeople({ ...filters, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function usePerson(id: string) {
  return useQuery({
    queryKey: peopleKeys.person(id),
    queryFn: () => getPerson(id),
    enabled: id.length > 0,
  });
}

/** What a person can see, and why (GET /people/:id/access). */
export function usePersonAccess(id: string) {
  return useQuery({
    queryKey: peopleKeys.personAccess(id),
    queryFn: () => getPersonAccess(id),
    enabled: id.length > 0,
  });
}

export function useOrgTree() {
  return useQuery({ queryKey: peopleKeys.orgTree, queryFn: getOrgTree });
}

export function useSite(code: string | null) {
  return useQuery({
    queryKey: peopleKeys.site(code ?? ''),
    queryFn: () => getSite(code ?? ''),
    enabled: code !== null,
  });
}

export function useQueueCounts(enabled = true) {
  return useQuery({ queryKey: peopleKeys.queues, queryFn: getQueueCounts, enabled });
}

export function useQueue(kind: QueueKind) {
  return useQuery({ queryKey: peopleKeys.queue(kind), queryFn: () => getQueue(kind) });
}
